import React, { useState } from 'react';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { storage } from '../../config/firebase';
import toast from 'react-hot-toast';

interface ImageUploadProps {
  sku: string;
  onImagesUploaded: (urls: string[]) => void;
  existingImages?: string[];
}

const ImageUpload: React.FC<ImageUploadProps> = ({ sku, onImagesUploaded, existingImages = [] }) => {
  const [uploading, setUploading] = useState(false);
  const [uploadedUrls, setUploadedUrls] = useState<string[]>(existingImages);

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    setUploading(true);
    const urls: string[] = [];

    try {
      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        
        // Create filename: SKU_01.jpg, SKU_02.jpg, etc.
        const imageNumber = (uploadedUrls.length + i + 1).toString().padStart(2, '0');
        const filename = `${sku}_${imageNumber}.${file.name.split('.').pop()}`;
        
        // Upload to Firebase Storage
        const storageRef = ref(storage, `auction-images/${filename}`);
        await uploadBytes(storageRef, file);
        
        // Get download URL
        const url = await getDownloadURL(storageRef);
        urls.push(url);
      }

      const allUrls = [...uploadedUrls, ...urls];
      setUploadedUrls(allUrls);
      onImagesUploaded(allUrls);
      
      toast.success(`${urls.length} image(s) uploaded successfully`);
    } catch (error) {
      console.error('Error uploading images:', error);
      toast.error('Failed to upload images');
    } finally {
      setUploading(false);
    }
  };

  const removeImage = (index: number) => {
    const newUrls = uploadedUrls.filter((_, i) => i !== index);
    setUploadedUrls(newUrls);
    onImagesUploaded(newUrls);
    toast.success('Image removed');
  };

  return (
    <div className="space-y-4">
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Upload Images for SKU: {sku}
        </label>
        <input
          type="file"
          accept="image/*"
          multiple
          onChange={handleFileSelect}
          disabled={uploading}
          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
        />
        <p className="text-xs text-gray-500 mt-1">
          Images will be named: {sku}_01.jpg, {sku}_02.jpg, etc.
        </p>
      </div>

      {uploading && (
        <div className="text-center py-4">
          <p className="text-primary-600">Uploading images...</p>
        </div>
      )}

      {uploadedUrls.length > 0 && (
        <div className="grid grid-cols-4 gap-4">
          {uploadedUrls.map((url, index) => (
            <div key={index} className="relative">
              <img
                src={url}
                alt={`${sku}_${index + 1}`}
                className="w-full h-32 object-cover rounded-lg"
              />
              <button
                onClick={() => removeImage(index)}
                className="absolute top-1 right-1 bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center hover:bg-red-600"
              >
                ×
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default ImageUpload;